import React, { useState, useRef, ReactNode } from 'react';

interface FastTooltipProps {
  children: ReactNode;
  tooltip: string;
  className?: string;
}

/**
 * Custom fast tooltip component with fixed positioning.
 * Designed for high-performance rendering in large tables.
 */
const FastTooltip: React.FC<FastTooltipProps> = ({
  children,
  tooltip,
  className = '',
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY });
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Set a delay before showing the tooltip
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 500); // 500ms delay
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    // Clear timeout and hide tooltip immediately
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowTooltip(false);
  };

  return (
    <>
      <div
        className={`${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {showTooltip && (
        <div
          className="fixed px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-xl border border-gray-700 pointer-events-none max-w-xs z-[10000]"
          style={{
            left: `${mousePos.x}px`,
            bottom: `${window.innerHeight - mousePos.y + 10}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="whitespace-normal break-words">{tooltip}</div>
        </div>
      )}
    </>
  );
};

export default FastTooltip;
